import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';
import { ChatComponent } from '../../components/chat/chat.component';
import { SplitPaneComponent } from '../../components/split-pane/split-pane.component';
import { SplitPaneVerticalComponent } from '../../components/split-pane-vertical/split-pane-vertical.component';
import { HistoryComponent } from '../../components/history/history.component';
import { DesignFacade } from '@state';

interface DesignBriefState {
  designSystem: string;
  accessibilityTarget: string;
  uxPriorities: string[];
}

@Component({
  selector: 'app-design',
  templateUrl: './design.page.html',
  styleUrls: ['./design.page.scss'],
  standalone: true,
  imports: [RouterModule, ChatComponent, SplitPaneComponent, SplitPaneVerticalComponent, HistoryComponent, FormsModule],
})
export class DesignPage implements OnInit {
  public designSuggestions: any[] = [
    { icon: 'bi bi-magic', prompt: 'Design a new Todo List app' },
  ];

  public designContext: any = {
    page: 'design',
    purpose: 'Create and iterate on UI/UX design concepts',
    tools: ['Figma'],
    designSystem: 'Appstrax',
    outputs: ['Design brief', 'Wireframes', 'Component list']
  };

  public designStore = inject(DesignFacade);
  public validationError = '';
  public successMessage = '';
  public generating = false;
  public hasGeneratedBrief = false;

  public designForm: DesignBriefState = {
    designSystem: '',
    accessibilityTarget: '',
    uxPriorities: [],
  };

  public readonly designSystemOptions = [
    'Appstrax',
    'Material-inspired',
    'Minimal neutral',
    'Bold brand-led',
  ];

  public readonly accessibilityOptions = [
    'WCAG 2.1 AA',
    'WCAG 2.2 AA',
    'WCAG 2.1 AAA',
  ];

  public readonly uxPriorityOptions = [
    'Fast onboarding',
    'Conversion clarity',
    'Power-user efficiency',
    'Mobile-first workflows',
    'Error prevention',
    'Visual consistency',
  ];

  constructor() {}

  async ngOnInit(): Promise<void> {
    await this.designStore.load();
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.designStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.designStore.logChatEvent(msg.text, msg.role);
  }

  public onPriorityToggle(priority: string, event: Event): void {
    const checked = !!(event.target as HTMLInputElement | null)?.checked;
    if (checked) {
      if (!this.designForm.uxPriorities.includes(priority)) {
        this.designForm.uxPriorities = [...this.designForm.uxPriorities, priority];
      }
      return;
    }
    this.designForm.uxPriorities = this.designForm.uxPriorities.filter((x) => x !== priority);
  }

  public get canGenerateBrief(): boolean {
    return !this.generating;
  }

  public async generateDesignBrief(): Promise<void> {
    this.validationError = '';
    this.successMessage = '';

    if (!this.designForm.designSystem.trim()) {
      this.validationError = 'Please choose a design system/style before generating a brief.';
      this.hasGeneratedBrief = false;
      return;
    }
    if (!this.designForm.accessibilityTarget.trim()) {
      this.validationError = 'Please choose an accessibility target before generating a brief.';
      this.hasGeneratedBrief = false;
      return;
    }
    if (this.designForm.uxPriorities.length < 1) {
      this.validationError = 'Select at least one UX priority to generate a useful brief.';
      this.hasGeneratedBrief = false;
      return;
    }

    this.generating = true;
    try {
      // Simulate generation to surface loading/success states without backend dependency.
      await new Promise((resolve) => setTimeout(resolve, 800));
      this.hasGeneratedBrief = true;
      this.successMessage = 'Design brief generated and ready for review.';
    } finally {
      this.generating = false;
    }
  }
}